import { useLazyQuery } from "@apollo/client";
import Preview from "@mui/icons-material/Preview";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DocumentPreview } from "components/Document";
import { HoverAction } from "components/HoverAction";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { DocumentType } from "generated-graphql/graphql";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { useMemo, useState } from "react";
import { prettyPrintDateMed } from "util/dates";

import { COMMUNICATION_DOCUMENTS_QUERY } from "../hooks/useCommunicationDocuments";
import { CommunicationDocument } from "../types";

export const DocumentsPickerDialog = ({
  tenantId,
  facilityIds,
  initialDocumentIds,
  close,
  saveDocuments,
}: {
  tenantId: string;
  facilityIds: string[];
  initialDocumentIds: string[];
  close: () => void;
  saveDocuments: (documents: CommunicationDocument[]) => void;
}) => {
  const alerts = useAlerts();
  const [documents, setDocuments] = useState<CommunicationDocument[]>([]);
  const [documentIds, setDocumentIds] = useState<string[]>(initialDocumentIds);
  const facilityFilter = useMemo(
    () => (facilityIds.length ? `facilityId:${facilityIds.join("|")}` : ""),
    [facilityIds]
  );
  const [previewFileUrl, setPreviewFileUrl] = useState<string | undefined>(undefined);
  const closePreview = () => setPreviewFileUrl(undefined);

  const [getDocumentDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data?.getDocumentDownloadLink) {
        return alerts.error("Error fetching download link");
      }
    },
    onError: (e) => {
      alerts.error("Error fetching download link", e);
    },
  });

  const columns: GridColDef<CommunicationDocument>[] = useMemo(() => {
    return [
      {
        field: "title",
        headerName: "Name",
        flex: 1.5,
        minWidth: 200,
        sortable: true,
        renderCell(params) {
          return (
            <Tooltip title={params.row.title}>
              <Box
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row.title}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "documentType",
        headerName: "Document Type",
        flex: 0.8,
        minWidth: 120,
        sortable: true,
        valueGetter(_, row) {
          return prettyPrintDocumentType(row.documentType);
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(DocumentType),
        enumPresentationFunction: (enumValue: string) =>
          prettyPrintDocumentType(enumValue as DocumentType),
      },
      {
        field: "fileExtension",
        headerName: "Format",
        flex: 0.5,
        minWidth: 80,
        sortable: true,
      },
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell(params) {
          const facilities = params.row.facilities ?? [];
          return (
            <Stack>
              {facilities.slice(0, 3).map((facility) => (
                <Typography key={facility.id} variant="body2">
                  {facility.name}
                </Typography>
              ))}
              {facilities.length > 3 && <Typography variant="body2">...</Typography>}
            </Stack>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded On",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        valueGetter(_, row) {
          return prettyPrintDateMed(row.createdAt);
        },
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.3,
        minWidth: 80,
        getActions: ({ row }) => [
          <Tooltip title="Preview Document" key="preview">
            <HoverAction
              onClick={async () => {
                const { data } = await getDocumentDownloadLink({
                  variables: { id: row.id },
                });

                if (
                  data?.getDocumentDownloadLink === null ||
                  data?.getDocumentDownloadLink === undefined
                ) {
                  alerts.error("Error fetching file url");
                  return;
                }
                setPreviewFileUrl(data.getDocumentDownloadLink);
              }}
              label="Preview Document"
              icon={<Preview fontSize="small" color="primary" />}
            />
          </Tooltip>,
        ],
      },
    ];
  }, [alerts, getDocumentDownloadLink]);

  return (
    <>
      <Dialog
        open
        maxWidth="md"
        onClose={close}
        title="Select Documents"
        description="Select existing documents from any associated facility and/or report"
        primaryActionButtonProps={{
          saveText: "Done",
          onClick: () => saveDocuments(documents),
          hideStartIcon: true,
        }}
      >
        <OmnisearchDataGrid
          persistenceKey="documents-picker"
          columns={columns}
          dataQuery={COMMUNICATION_DOCUMENTS_QUERY}
          skip={!tenantId}
          initialSortModel={[{ field: "createdAt", sort: "desc" }]}
          defaultSearch={`tenantId:${tenantId} ${facilityFilter}`.trim()}
          getItems={(data) => data.documents.items}
          getCount={(data) => data.documents.count ?? 0}
          noDataMessage="No documents found for this organization."
          isURLDriven={false}
          selectedIds={documentIds}
          disableRowSelectionOnClick={false}
          onSelectedIdsChanged={(newDocumentIds) => {
            setDocumentIds(newDocumentIds as string[]);
          }}
          onSelectedRowsChanged={(documents) => {
            setDocuments(documents);
          }}
          initialPageSize={10}
          withPadding={false}
        />
      </Dialog>
      {!!previewFileUrl && (
        <Dialog
          open
          onClose={closePreview}
          fullWidth
          maxWidth="lg"
          title="Document Preview"
          noPrimaryAction
        >
          <DocumentPreview fileUrl={previewFileUrl} />
        </Dialog>
      )}
    </>
  );
};
